import { type CategoryTree } from '@/shared/category/type';
import { useBuildFormContext } from '@/views/build/form/schema';

import { type CategoryInfoInputType } from '../../category-info/types';

/**
 * 카테고리 정보를 form과 연동하기 위한 hook
 *
 * QuizSetupModal과 CategoryInfoModal 모두에서 사용됩니다.
 * - getCategoryInfoValues: 현재 form의 카테고리 정보 값을 모달 초기값으로 변환
 * - updateCategoryInfoValues: 모달에서 완료된 카테고리 정보를 form에 반영하고 자동 타이틀 생성
 */
export const useGetCategoryFormValue = () => {
  const { setValue, getValues } = useBuildFormContext();

  /**
   * 현재 form 값을 모달 초기값으로 변환
   * schoolInfoTree를 받아서 현재 국가에 맞지 않는 데이터는 필터링
   */
  const getCategoryFormValue = (
    schoolInfoTree?: CategoryTree,
  ): CategoryInfoInputType => {
    const currentSchoolTypeId = getValues('schoolTypeId');

    // schoolInfoTree가 제공되고, schoolTypeId가 현재 트리에 없으면 모든 교육정보 초기화
    const shouldReset =
      schoolInfoTree &&
      !isSchoolTypeIdValidForTree(currentSchoolTypeId, schoolInfoTree);

    if (shouldReset) {
      return {
        schoolTypeId: '',
        gradeId: undefined,
        semesterId: undefined,
        subject: '',
        subSubject: '',
        publisher: 'none',
        customPublisher: '',
        isLastSchoolInfoSaved: getValues('isLastSchoolInfoSaved'),
      };
    }

    return {
      schoolTypeId: currentSchoolTypeId,
      gradeId: getValues('gradeId'),
      semesterId: getValues('semesterId'),
      subject: getValues('subject'),
      subSubject: getValues('subSubject'),
      publisher: getValues('publisher'),
      customPublisher: getValues('customPublisher'),
      isLastSchoolInfoSaved: getValues('isLastSchoolInfoSaved'),
    };
  };

  /**
   * 모달에서 완료된 카테고리 정보를 form에 반영
   * - 모든 카테고리 정보 필드를 form에 반영하고 validation 실행
   *
   * @param values - 모달에서 입력된 카테고리 정보
   * @param generatedTitle - (사용되지 않음, 호환성을 위해 유지)
   */
  const updateCategoryFormValue = (values: CategoryInfoInputType) => {
    // 카테고리 정보를 form에 반영
    const categoryFields = [
      'schoolTypeId',
      'gradeId',
      'semesterId',
      'subject',
      'subSubject',
      'publisher',
      'customPublisher',
      'isLastSchoolInfoSaved',
    ] as const;

    categoryFields.forEach(field => {
      setValue(field, values[field], {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    });
  };

  return {
    getCategoryFormValue,
    updateCategoryFormValue,
  };
};

/**
 * schoolTypeId가 현재 schoolInfoTree에 존재하는지 확인
 */
const isSchoolTypeIdValidForTree = (
  schoolTypeId: string | undefined,
  schoolInfoTree: CategoryTree,
): boolean => {
  if (!schoolTypeId) return true; // 빈 값은 valid

  // schoolInfoTree의 children에서 schoolTypeId가 존재하는지 확인
  return schoolInfoTree.children.some(
    child => child.categoryTreeId?.toString() === schoolTypeId,
  );
};
